/**
 * BS 7671:2018+A2:2022 Max Zs Lookup Tables
 * 
 * Maximum Earth Fault Loop Impedance (Zs) values for:
 * - 0.4s disconnection time (most circuits)
 * - 5s disconnection time (final circuits ≤ 32A)
 * 
 * Based on:
 * - Table 41.2: Maximum Zs values for AC a.c. circuits (230V)
 * - Table 41.3: Maximum Zs values for AC a.c. circuits (400V)
 * - Table 41.4: Maximum Zs values for final circuits exceeding 32A
 * - Table 41.6: Maximum Zs values for circuits supplying sockets for portable equipment
 * 
 * Voltage Factor (Cmin) = 0.95 (for 230V nominal)
 * Nominal Voltage (U0) = 230V
 */

/**
 * BS 7671 Table 41.2 - Maximum Zs values for overcurrent protective devices
 * Disconnection time: 0.4s (most circuits)
 * Disconnection time: 5s (final circuits up to 32A)
 */

// MCB Types: BS EN 60898 (Circuit breakers)
const MCB_TYPES = {
  TYPE_B: { cFactor: 5, name: 'Type B' },
  TYPE_C: { cFactor: 10, name: 'Type C' },
  TYPE_D: { cFactor: 20, name: 'Type D' }
};

// Fuses: BS 88-2, BS 88-3, BS 3036 (Rewirable), BS 1361 (13A plug fuse)
const FUSE_TYPES = {
  BS_88_2: { name: 'BS 88-2 (HRC)' },
  BS_88_3: { name: 'BS 88-3 (HRC)' },
  BS_3036: { name: 'BS 3036 (Rewirable)' },
  BS_1361: { name: 'BS 1361 (13A Plug)' }
};

// RCBO Types: BS EN 61009 & BS EN 61008
const RCBO_TYPES = {
  TYPE_B: { name: 'RCBO Type B' },
  TYPE_C: { name: 'RCBO Type C' },
  TYPE_D: { name: 'RCBO Type D' },
  TYPE_K: { name: 'RCBO Type K' }
};

/**
 * BS 7671 Table 41.2 - Maximum Zs values (0.4s disconnection)
 * Nominal Voltage: 230V
 * Voltage Factor (Cmin): 0.95
 * 
 * U0 = 230V (phase to earth voltage)
 * Zs max = (U0 * Cmin) / Ia
 * 
 * Where Ia is the minimum current causing operation within 0.4s
 */
const MAX_ZS_04S_230V = {
  // MCB Type B (Ia = 5 x In at 0.4s)
  'MCB Type B 6A': { zS: 37.7 },   // 230 * 0.95 / (5 * 6) = 37.7
  'MCB Type B 10A': { zS: 21.6 },
  'MCB Type B 16A': { zS: 13.5 },
  'MCB Type B 20A': { zS: 10.8 },
  'MCB Type B 25A': { zS: 8.6 },
  'MCB Type B 32A': { zS: 6.7 },
  'MCB Type B 40A': { zS: 5.4 },
  'MCB Type B 50A': { zS: 4.3 },
  'MCB Type B 63A': { zS: 3.4 },
  
  // MCB Type C (Ia = 10 x In at 0.4s)
  'MCB Type C 6A': { zS: 18.8 },
  'MCB Type C 10A': { zS: 10.8 },
  'MCB Type C 16A': { zS: 6.7 },
  'MCB Type C 20A': { zS: 5.4 },
  'MCB Type C 25A': { zS: 4.3 },
  'MCB Type C 32A': { zS: 3.4 },
  'MCB Type C 40A': { zS: 2.7 },
  'MCB Type C 50A': { zS: 2.1 },
  'MCB Type C 63A': { zS: 1.7 },
  
  // MCB Type D (Ia = 20 x In at 0.4s)
  'MCB Type D 6A': { zS: 9.4 },
  'MCB Type D 10A': { zS: 5.4 },
  'MCB Type D 16A': { zS: 3.4 },
  'MCB Type D 20A': { zS: 2.7 },
  'MCB Type D 25A': { zS: 2.1 },
  'MCB Type D 32A': { zS: 1.7 },
  'MCB Type D 40A': { zS: 1.3 },
  'MCB Type D 50A': { zS: 1.1 },
  'MCB Type D 63A': { zS: 0.85 },

  // RCBO Type B (Ia = 30mA for RCD function, but MCB portion follows Type B)
  'RCBO Type B 6A': { zS: 37.7 },
  'RCBO Type B 10A': { zS: 21.6 },
  'RCBO Type B 16A': { zS: 13.5 },
  'RCBO Type B 20A': { zS: 10.8 },
  'RCBO Type B 25A': { zS: 8.6 },
  'RCBO Type B 32A': { zS: 6.7 },

  // RCBO Type C
  'RCBO Type C 6A': { zS: 18.8 },
  'RCBO Type C 10A': { zS: 10.8 },
  'RCBO Type C 16A': { zS: 6.7 },
  'RCBO Type C 20A': { zS: 5.4 },
  'RCBO Type C 25A': { zS: 4.3 },
  'RCBO Type C 32A': { zS: 3.4 },

  // RCBO Type D
  'RCBO Type D 6A': { zS: 9.4 },
  'RCBO Type D 10A': { zS: 5.4 },
  'RCBO Type D 16A': { zS: 3.4 },
  'RCBO Type D 20A': { zS: 2.7 },
  'RCBO Type D 25A': { zS: 2.1 },
  'RCBO Type D 32A': { zS: 1.7 },

  // BS 88-2 HRC Fuses (Ia = 2.5 x In at 0.4s for In ≤ 63A)
  'BS 88-2 6A': { zS: 75.3 },
  'BS 88-2 10A': { zS: 45.2 },
  'BS 88-2 16A': { zS: 28.2 },
  'BS 88-2 20A': { zS: 22.6 },
  'BS 88-2 25A': { zS: 18.1 },
  'BS 88-2 32A': { zS: 14.1 },
  'BS 88-2 40A': { zS: 11.3 },
  'BS 88-2 50A': { zS: 9.0 },
  'BS 88-2 63A': { zS: 7.1 },

  // BS 88-3 HRC Fuses (similar to BS 88-2)
  'BS 88-3 6A': { zS: 75.3 },
  'BS 88-3 10A': { zS: 45.2 },
  'BS 88-3 16A': { zS: 28.2 },
  'BS 88-3 20A': { zS: 22.6 },
  'BS 88-3 25A': { zS: 18.1 },
  'BS 88-3 32A': { zS: 14.1 },
  'BS 88-3 40A': { zS: 11.3 },
  'BS 88-3 50A': { zS: 9.0 },
  'BS 88-3 63A': { zS: 7.1 },

  // BS 3036 Rewirable Fuses (Ia = 2.0 x In at 0.4s)
  'BS 3036 6A': { zS: 94.2 },
  'BS 3036 10A': { zS: 56.5 },
  'BS 3036 16A': { zS: 35.3 },
  'BS 3036 20A': { zS: 28.2 },
  'BS 3036 25A': { zS: 22.6 },
  'BS 3036 32A': { zS: 17.7 },

  // BS 1361 13A Plug Fuse (Ia = 2.0 x 13A = 26A at 0.4s)
  'BS 1361 13A': { zS: 7.2 }
};

/**
 * BS 7671 Table 41.2 - Maximum Zs values (5s disconnection)
 * For final circuits not exceeding 32A
 */
const MAX_ZS_5S_230V = {
  // MCB Type B (Ia = 5 x In at 5s)
  'MCB Type B 6A': { zS: 45.3 },
  'MCB Type B 10A': { zS: 27.6 },
  'MCB Type B 16A': { zS: 17.3 },
  'MCB Type B 20A': { zS: 13.8 },
  'MCB Type B 25A': { zS: 11.0 },
  'MCB Type B 32A': { zS: 8.6 },
  
  // MCB Type C (Ia = 10 x In at 5s)
  'MCB Type C 6A': { zS: 22.6 },
  'MCB Type C 10A': { zS: 13.8 },
  'MCB Type C 16A': { zS: 8.6 },
  'MCB Type C 20A': { zS: 6.9 },
  'MCB Type C 25A': { zS: 5.5 },
  'MCB Type C 32A': { zS: 4.3 },
  
  // MCB Type D (Ia = 20 x In at 5s)
  'MCB Type D 6A': { zS: 11.3 },
  'MCB Type D 10A': { zS: 6.9 },
  'MCB Type D 16A': { zS: 4.3 },
  'MCB Type D 20A': { zS: 3.5 },
  'MCB Type D 25A': { zS: 2.8 },
  'MCB Type D 32A': { zS: 2.2 },

  // RCBO Type B
  'RCBO Type B 6A': { zS: 45.3 },
  'RCBO Type B 10A': { zS: 27.6 },
  'RCBO Type B 16A': { zS: 17.3 },
  'RCBO Type B 20A': { zS: 13.8 },
  'RCBO Type B 25A': { zS: 11.0 },
  'RCBO Type B 32A': { zS: 8.6 },

  // RCBO Type C
  'RCBO Type C 6A': { zS: 22.6 },
  'RCBO Type C 10A': { zS: 13.8 },
  'RCBO Type C 16A': { zS: 8.6 },
  'RCBO Type C 20A': { zS: 6.9 },
  'RCBO Type C 25A': { zS: 5.5 },
  'RCBO Type C 32A': { zS: 4.3 },

  // RCBO Type D
  'RCBO Type D 6A': { zS: 11.3 },
  'RCBO Type D 10A': { zS: 6.9 },
  'RCBO Type D 16A': { zS: 4.3 },
  'RCBO Type D 20A': { zS: 3.5 },
  'RCBO Type D 25A': { zS: 2.8 },
  'RCBO Type D 32A': { zS: 2.2 },

  // BS 88-2 HRC Fuses
  'BS 88-2 6A': { zS: 90.4 },
  'BS 88-2 10A': { zS: 54.2 },
  'BS 88-2 16A': { zS: 33.9 },
  'BS 88-2 20A': { zS: 27.1 },
  'BS 88-2 25A': { zS: 21.7 },
  'BS 88-2 32A': { zS: 17.0 },
  'BS 88-2 40A': { zS: 13.5 },
  'BS 88-2 50A': { zS: 10.8 },
  'BS 88-2 63A': { zS: 8.5 },

  // BS 3036 Rewirable Fuses
  'BS 3036 6A': { zS: 113.0 },
  'BS 3036 10A': { zS: 67.8 },
  'BS 3036 16A': { zS: 42.4 },
  'BS 3036 20A': { zS: 33.9 },
  'BS 3036 25A': { zS: 27.1 },
  'BS 3036 32A': { zS: 21.2 },

  // BS 1361 13A Plug Fuse
  'BS 1361 13A': { zS: 8.6 }
};

/**
 * BS 7671 Table 41.3 - Maximum Zs values for 400V systems
 * Disconnection time: 0.4s
 * Nominal Voltage: 400V (line-to-line)
 * Voltage Factor (Cmin): 0.95
 * U0 = 400V / √3 = 230V (same as 230V systems for Zs calculation)
 */
const MAX_ZS_04S_400V = {
  // For 400V three-phase systems, Zs values are the same as 230V
  // because Zs is calculated phase-to-earth (230V)
  ...MAX_ZS_04S_230V
};

/**
 * BS 7671 Table 41.4 - Maximum Zs values for circuits > 32A
 * Disconnection time: 5s (for protection of fixed equipment)
 */
const MAX_ZS_5S_HIGH_CURRENT = {
  // For circuits exceeding 32A, 5s disconnection time applies
  // Using Ia = 2.5 x In for fuses and MCBs
  'MCB Type B 40A': { zS: 43.7 },
  'MCB Type B 50A': { zS: 35.0 },
  'MCB Type B 63A': { zS: 27.6 },
  'MCB Type B 80A': { zS: 21.9 },
  'MCB Type B 100A': { zS: 17.5 },
  'MCB Type B 125A': { zS: 14.0 },

  'MCB Type C 40A': { zS: 21.9 },
  'MCB Type C 50A': { zS: 17.5 },
  'MCB Type C 63A': { zS: 13.9 },
  'MCB Type C 80A': { zS: 11.0 },
  'MCB Type C 100A': { zS: 8.8 },
  'MCB Type C 125A': { zS: 7.0 },

  'MCB Type D 40A': { zS: 11.0 },
  'MCB Type D 50A': { zS: 8.8 },
  'MCB Type D 63A': { zS: 7.0 },
  'MCB Type D 80A': { zS: 5.5 },
  'MCB Type D 100A': { zS: 4.4 },
  'MCB Type D 125A': { zS: 3.5 },

  'BS 88-2 63A': { zS: 85.0 },
  'BS 88-2 80A': { zS: 67.0 },
  'BS 88-2 100A': { zS: 53.5 },
  'BS 88-2 125A': { zS: 42.8 },

  'BS 3036 45A': { zS: 101.0 },
  'BS 3036 60A': { zS: 75.8 },
  'BS 3036 80A': { zS: 56.9 },
  'BS 3036 100A': { zS: 45.5 },
  'BS 3036 125A': { zS: 36.4 }
};

/**
 * BS 7671 Table 41.6 - Maximum Zs for portable equipment (30mA RCD)
 * Disconnection time: 0.2s (for 30mA RCD)
 * Ia = 100mA (5 x 30mA) for 0.2s disconnection
 * Zs max = (230 * 0.95) / 0.1 = 2185 Ω
 */
const MAX_ZS_02S_30MA_RCD = {
  '30mA RCD': { zS: 2185 } // For portable equipment protected by 30mA RCD
};

/**
 * BS 7671 Table 41.7 - Maximum Zs for final circuits with RCD protection
 * Disconnection time: 0.2s (30mA), 0.4s (100mA), 0.5s (300mA)
 */
const MAX_ZS_RCD_PROTECTED = {
  '30mA RCD 0.2s': { zS: 2185 },
  '30mA RCD 0.4s': { zS: 2185 }, // Same, Ia doesn't change significantly
  '100mA RCD 0.4s': { zS: 655 },  // (230 * 0.95) / 0.333 ≈ 655
  '300mA RCD 0.5s': { zS: 218 }   // (230 * 0.95) / 1.0 ≈ 218
};

/**
 * Zs Calculation Engine
 */
class ZsCalculator {
  /**
   * Calculate maximum permitted Zs for a given protective device
   * @param {Object} device - Protective device configuration
   * @param {string} device.type - 'MCB', 'RCBO', 'Fuse'
   * @param {string} device.typeVariant - 'Type B', 'Type C', 'Type D', etc.
   * @param {number} device.rating - Nominal current rating (In) in Amps
   * @param {string} device.disconnectionTime - '0.4s' or '5s'
   * @param {boolean} device.is30mARCD - True if protected by 30mA RCD
   * @returns {number|null} - Maximum permitted Zs in ohms, or null if not found
   */
  static getMaxZs(device) {
    const {
      type,
      typeVariant,
      rating,
      disconnectionTime = '0.4s',
      is30mARCD = false
    } = device;

    // Handle 30mA RCD protected circuits (Table 41.7)
    if (is30mARCD) {
      const key = `30mA RCD ${disconnectionTime}`;
      return MAX_ZS_RCD_PROTECTED[key]?.zS || MAX_ZS_02S_30MA_RCD['30mA RCD']?.zS;
    }

    // Select appropriate table based on disconnection time
    const table = disconnectionTime === '5s' 
      ? MAX_ZS_5S_230V 
      : MAX_ZS_04S_230V;

    // Build key from device configuration
    let key;
    if (type === 'MCB') {
      key = `MCB ${typeVariant} ${rating}A`;
    } else if (type === 'RCBO') {
      key = `RCBO ${typeVariant} ${rating}A`;
    } else if (type === 'Fuse') {
      if (typeVariant === 'BS 1361') {
        key = 'BS 1361 13A';
      } else if (typeVariant === 'BS 88-2' || typeVariant === 'BS 88-3') {
        key = `${typeVariant} ${rating}A`;
      } else if (typeVariant === 'BS 3036') {
        key = `BS 3036 ${rating}A`;
      }
    }

    return table?.[key]?.zS || null;
  }

  /**
   * Calculate actual Zs from measured values
   * Zs = Ze + (R1 + R2)
   * @param {number} Ze - Earth electrode resistance (Ω)
   * @param {number} R1 - Line conductor resistance (Ω)
   * @param {number} R2 - CPC resistance (Ω)
   * @param {number} temperatureFactor - Temperature correction factor (default: 1.20 for 20°C)
   * @returns {number} - Calculated Zs in ohms
   */
  static calculateActualZs(Ze, R1, R2, temperatureFactor = 1.20) {
    return Ze + (R1 + R2) * temperatureFactor;
  }

  /**
   * Calculate temperature correction factor
   * BS 7671: 20°C base, correction factor = 1.20 for typical ambient temperatures
   * @param {number} actualTemp - Actual conductor temperature in °C
   * @param {number} baseTemp - Base temperature (default: 20°C)
   * @returns {number} - Temperature correction factor
   */
  static getTemperatureCorrectionFactor(actualTemp = 20, baseTemp = 20) {
    // For copper conductors:
    // Temperature coefficient = 234.5 for copper
    // Correction factor = (234.5 + baseTemp) / (234.5 + actualTemp)
    const k = 234.5; // Copper temperature coefficient
    return (k + baseTemp) / (k + actualTemp);
  }

  /**
   * Check if actual Zs is within permitted limit
   * @param {number} actualZs - Calculated actual Zs
   * @param {number} maxZs - Maximum permitted Zs
   * @param {number} tolerance - Allowable tolerance percentage (default: 5%)
   * @returns {Object} - { isCompliant: boolean, margin: number, status: string }
   */
  static checkZsCompliance(actualZs, maxZs, tolerance = 0.05) {
    if (!actualZs || !maxZs) {
      return { 
        isCompliant: false, 
        margin: 0, 
        status: 'insufficient-data',
        warning: 'Missing actual Zs or maximum Zs value'
      };
    }

    const threshold = maxZs * (1 - tolerance);
    const margin = ((maxZs - actualZs) / maxZs) * 100;
    
    let status;
    if (actualZs <= threshold) {
      status = 'compliant';
    } else if (actualZs <= maxZs) {
      status = 'borderline';
    } else {
      status = 'non-compliant';
    }

    return {
      isCompliant: actualZs <= threshold,
      margin: Math.round(margin * 10) / 10,
      status,
      actualZs: Math.round(actualZs * 100) / 100,
      maxZs: Math.round(maxZs * 100) / 100,
      threshold: Math.round(threshold * 100) / 100
    };
  }

  /**
   * Get all available MCB types with their Zs values
   * @returns {Array} - Array of MCB configurations
   */
  static getAvailableMCBConfigs() {
    const configs = [];
    
    ['Type B', 'Type C', 'Type D'].forEach(typeVariant => {
      [6, 10, 16, 20, 25, 32, 40, 50, 63].forEach(rating => {
        configs.push({
          type: 'MCB',
          typeVariant,
          rating,
          maxZs04s: MAX_ZS_04S_230V[`MCB ${typeVariant} ${rating}A`]?.zS,
          maxZs5s: MAX_ZS_5S_230V[`MCB ${typeVariant} ${rating}A`]?.zS
        });
      });
    });
    
    return configs;
  }

  /**
   * Get all available RCBO types with their Zs values
   * @returns {Array} - Array of RCBO configurations
   */
  static getAvailableRCBOConfigs() {
    const configs = [];
    
    ['Type B', 'Type C', 'Type D'].forEach(typeVariant => {
      [6, 10, 16, 20, 25, 32].forEach(rating => {
        configs.push({
          type: 'RCBO',
          typeVariant,
          rating,
          maxZs04s: MAX_ZS_04S_230V[`RCBO ${typeVariant} ${rating}A`]?.zS,
          maxZs5s: MAX_ZS_5S_230V[`RCBO ${typeVariant} ${rating}A`]?.zS
        });
      });
    });
    
    return configs;
  }

  /**
   * Get all available Fuse types with their Zs values
   * @returns {Array} - Array of Fuse configurations
   */
  static getAvailableFuseConfigs() {
    const configs = [];
    
    // BS 88-2/88-3
    [6, 10, 16, 20, 25, 32, 40, 50, 63].forEach(rating => {
      configs.push({
        type: 'Fuse',
        typeVariant: 'BS 88-2',
        rating,
        maxZs04s: MAX_ZS_04S_230V[`BS 88-2 ${rating}A`]?.zS,
        maxZs5s: MAX_ZS_5S_230V[`BS 88-2 ${rating}A`]?.zS
      });
      configs.push({
        type: 'Fuse',
        typeVariant: 'BS 88-3',
        rating,
        maxZs04s: MAX_ZS_04S_230V[`BS 88-3 ${rating}A`]?.zS,
        maxZs5s: MAX_ZS_5S_230V[`BS 88-3 ${rating}A`]?.zS
      });
    });
    
    // BS 3036
    [6, 10, 16, 20, 25, 32, 45, 60, 80, 100, 125].forEach(rating => {
      configs.push({
        type: 'Fuse',
        typeVariant: 'BS 3036',
        rating,
        maxZs04s: MAX_ZS_04S_230V[`BS 3036 ${rating}A`]?.zS,
        maxZs5s: MAX_ZS_5S_230V[`BS 3036 ${rating}A`]?.zS
      });
    });
    
    // BS 1361
    configs.push({
      type: 'Fuse',
      typeVariant: 'BS 1361',
      rating: 13,
      maxZs04s: MAX_ZS_04S_230V['BS 1361 13A']?.zS,
      maxZs5s: MAX_ZS_5S_230V['BS 1361 13A']?.zS
    });
    
    return configs;
  }
}

/**
 * Export all tables and calculator
 */
export {
  // Tables
  MAX_ZS_04S_230V,
  MAX_ZS_5S_230V,
  MAX_ZS_04S_400V,
  MAX_ZS_5S_HIGH_CURRENT,
  MAX_ZS_02S_30MA_RCD,
  MAX_ZS_RCD_PROTECTED,
  
  // Device types
  MCB_TYPES,
  FUSE_TYPES,
  RCBO_TYPES,
  
  // Calculator
  ZsCalculator
};

export default ZsCalculator;
